import { z } from "zod";

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const getNestedValue = (obj: any, path: string): any => {
	return path.split(".").reduce((current, key) => current?.[key], obj);
};

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const hasOwnPath = (obj: any, path: string): boolean => {
	const parts = path.split(".");
	let current = obj;
	for (const key of parts) {
		if (current === null || typeof current !== "object" || !(key in current)) {
			return false;
		}
		current = current[key];
	}
	return true;
};

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const isEmpty = (v: any): boolean =>
	v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export const hasPartialMeta = (schema: z.ZodType): boolean => schema.meta()?.partial === true;

export const hasRequiredForApprovalMeta = (schema: z.ZodType): boolean =>
	schema.meta()?.requiredForApproval === true;

type Wrappable = z.ZodOptional<z.ZodType> | z.ZodNullable<z.ZodType> | z.ZodDefault<z.ZodType>;
type TypedArray = z.ZodArray<z.ZodType>;

const isWrapped = (schema: z.ZodType): schema is Wrappable =>
	schema instanceof z.ZodOptional ||
	schema instanceof z.ZodNullable ||
	schema instanceof z.ZodDefault;

const isArray = (schema: z.ZodType): schema is TypedArray => schema instanceof z.ZodArray;

export const unwrapSchema = (schema: z.ZodType): z.ZodType => {
	if (isWrapped(schema)) return unwrapSchema(schema.unwrap());
	return schema;
};

export const navigateToField = (schema: z.ZodType, fieldPath: string): z.ZodType | null => {
	const parts = fieldPath.split(".");
	let current: z.ZodType = schema;

	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		const unwrapped = unwrapSchema(current);
		const isNumeric = /^\d+$/.test(part);

		if (unwrapped instanceof z.ZodObject) {
			const field = unwrapped.shape[part];
			if (!field) return null;
			current = field;
			continue;
		}

		if (isNumeric && isArray(unwrapped)) {
			current = unwrapped.element;
			continue;
		}

		if (unwrapped instanceof z.ZodDiscriminatedUnion || unwrapped instanceof z.ZodUnion) {
			const remainingPath = parts.slice(i).join(".");
			for (const option of unwrapped.options as z.ZodType[]) {
				const result = navigateToField(option, remainingPath);
				if (result) return result;
			}
			return null;
		}

		const meta = unwrapped.meta?.();
		if (meta?.__variants && meta?.__discriminator) {
			const remainingPath = parts.slice(i).join(".");
			for (const variant of meta.__variants as z.ZodObject[]) {
				const result = navigateToField(variant, remainingPath);
				if (result) return result;
			}
			return null;
		}

		return null;
	}

	return current;
};

export const isFieldPartial = (schema: z.ZodType, fieldPath: string): boolean => {
	const field = navigateToField(schema, fieldPath);
	if (!field) return false;
	return hasPartialMeta(field) && !hasRequiredForApprovalMeta(field);
};

export const isFieldRequiredForApproval = (schema: z.ZodType, fieldPath: string): boolean => {
	const field = navigateToField(schema, fieldPath);
	if (!field) return false;
	return hasRequiredForApprovalMeta(field);
};

const isOptionalForSubmit = (field: z.ZodType): boolean => {
	if (
		field instanceof z.ZodOptional ||
		field instanceof z.ZodNullable ||
		field instanceof z.ZodDefault
	) {
		return true;
	}
	// Piped schemas (e.g. unique refine → nullish brand) are optional when the output is.
	if (field instanceof z.ZodPipe && field.out instanceof z.ZodType) {
		return isOptionalForSubmit(field.out);
	}
	return false;
};

/** True when the field blocks form submit if empty (hard-required). Soft-required partial/RFA fields never qualify. */
export const isFieldRequiredForSubmit = (schema: z.ZodType, fieldPath: string): boolean => {
	const field = navigateToField(schema, fieldPath);
	if (!field) return false;
	if (hasPartialMeta(field) || hasRequiredForApprovalMeta(field)) return false;
	if (isOptionalForSubmit(field)) return false;
	return true;
};

export const findMatchingVariants = (
	variants: z.ZodObject[],
	discriminatorKey: string,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	values: any,
): z.ZodObject[] => {
	const discValue = values?.[discriminatorKey];
	if (discValue === undefined || discValue === null) return variants;

	const match = variants.find((v) => {
		const discField = v.shape?.[discriminatorKey];
		if (!discField) return false;
		if (discField instanceof z.ZodLiteral) return discField.values.has(discValue);
		return false;
	});

	return match ? [match] : variants;
};

type CollectPathsOptions = {
	treatArrayAsLeaf?: boolean;
};

const collectPaths = (
	schema: z.ZodType,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	valuesAtPath: any,
	prefix: string,
	check: (field: z.ZodType) => boolean,
	options: CollectPathsOptions = {},
): string[] => {
	const paths: string[] = [];
	const unwrapped = unwrapSchema(schema);

	const schemaMeta = schema.meta?.();
	if (schemaMeta?.__variants && schemaMeta?.__discriminator) {
		for (const variant of findMatchingVariants(
			schemaMeta.__variants as z.ZodObject[],
			schemaMeta.__discriminator as string,
			valuesAtPath,
		)) {
			paths.push(...collectPaths(variant, valuesAtPath, prefix, check, options));
		}
		return paths;
	}

	if (unwrapped instanceof z.ZodObject) {
		for (const [key, field] of Object.entries(unwrapped.shape) as [string, z.ZodType][]) {
			const currentPath = prefix ? `${prefix}.${key}` : key;
			const subValues = valuesAtPath?.[key];

			if (check(field)) {
				paths.push(currentPath);
			}

			const fieldMeta = field.meta?.();
			if (fieldMeta?.__variants && fieldMeta?.__discriminator) {
				for (const variant of findMatchingVariants(
					fieldMeta.__variants as z.ZodObject[],
					fieldMeta.__discriminator as string,
					subValues,
				)) {
					paths.push(...collectPaths(variant, subValues, currentPath, check, options));
				}
				continue;
			}

			const inner = unwrapSchema(field);

			if (inner instanceof z.ZodObject) {
				paths.push(...collectPaths(inner, subValues, currentPath, check, options));
			} else if (isArray(inner)) {
				if (options.treatArrayAsLeaf) continue;
				if (Array.isArray(subValues)) {
					for (let i = 0; i < subValues.length; i++) {
						paths.push(
							...collectPaths(inner.element, subValues[i], `${currentPath}.${i}`, check, options),
						);
					}
				}
			} else if (inner instanceof z.ZodDiscriminatedUnion) {
				const disc = (inner.def as { discriminator?: string }).discriminator;
				const variants = disc
					? findMatchingVariants(inner.options as z.ZodObject[], disc, subValues)
					: (inner.options as z.ZodType[]);
				for (const option of variants) {
					paths.push(...collectPaths(option, subValues, currentPath, check, options));
				}
			} else if (inner instanceof z.ZodUnion) {
				for (const option of inner.options as z.ZodType[]) {
					paths.push(...collectPaths(option, subValues, currentPath, check, options));
				}
			}
		}
	} else if (unwrapped instanceof z.ZodDiscriminatedUnion) {
		const disc = (unwrapped.def as { discriminator?: string }).discriminator;
		const variants = disc
			? findMatchingVariants(unwrapped.options as z.ZodObject[], disc, valuesAtPath)
			: (unwrapped.options as z.ZodType[]);
		for (const option of variants) {
			paths.push(...collectPaths(option, valuesAtPath, prefix, check, options));
		}
	} else if (unwrapped instanceof z.ZodUnion) {
		for (const option of unwrapped.options as z.ZodType[]) {
			paths.push(...collectPaths(option, valuesAtPath, prefix, check, options));
		}
	}

	return paths;
};

const isPartialCheck = (field: z.ZodType) =>
	hasPartialMeta(field) && !hasRequiredForApprovalMeta(field);

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const getPartialFieldPaths = (schema: z.ZodType, values?: any, prefix = ""): string[] => [
	...new Set(collectPaths(schema, values, prefix, isPartialCheck)),
];

export const getRequiredForApprovalFieldPaths = (
	schema: z.ZodType,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	values?: any,
	prefix = "",
): string[] => [...new Set(collectPaths(schema, values, prefix, hasRequiredForApprovalMeta))];

export const getPartialFields = (schema: z.ZodType): string[] => getPartialFieldPaths(schema);

export const getEmptyPartialFieldPaths = (schema: z.ZodType, values: unknown): string[] =>
	getPartialFieldPaths(schema, values).filter((path) => isEmpty(getNestedValue(values, path)));

/**
 * True when a required-for-approval field is blank. Unlike `getEmptyPartialFieldPaths`, a path the
 * values object does not carry is skipped rather than counted as blank, so a union variant's field
 * is not reported missing when the values belong to another variant.
 */
export const hasMissingRequiredForApprovalFields = (schema: z.ZodType, values: unknown): boolean =>
	getRequiredForApprovalFieldPaths(schema, values).some(
		(path) => hasOwnPath(values, path) && isEmpty(getNestedValue(values, path)),
	);

const isLeafFormField = (field: z.ZodType): boolean => {
	const meta = field.meta?.();
	if (meta?.__variants && meta?.__discriminator) return false;
	const unwrapped = unwrapSchema(field);
	if (unwrapped instanceof z.ZodObject) return false;
	if (unwrapped instanceof z.ZodDiscriminatedUnion) return false;
	if (unwrapped instanceof z.ZodUnion) return false;
	return true;
};

export const getAllFormFieldPaths = (
	schema: z.ZodType,
	values?: unknown,
	prefix = "",
): string[] => [
	...new Set(collectPaths(schema, values, prefix, isLeafFormField, { treatArrayAsLeaf: true })),
];

export const getEmptyFormFieldPaths = (schema: z.ZodType, values: unknown): string[] =>
	getAllFormFieldPaths(schema, values).filter((path) => isEmpty(getNestedValue(values, path)));

/**
 * Ratio (0-1) of the schema's form fields that have a value, across every field in the
 * DTO rather than only the partial-tagged ones. Undefined when the schema has no fields.
 */
export const getFormCompletion = (schema: z.ZodType, values: unknown): number | undefined => {
	const paths = getAllFormFieldPaths(schema, values);
	if (paths.length === 0) return undefined;
	const emptyCount = paths.filter((path) => isEmpty(getNestedValue(values, path))).length;
	return (paths.length - emptyCount) / paths.length;
};

export const getMissingRequiredForApprovalLabels = (
	schema: z.ZodType,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	values: any,
	prefix = "",
): string[] => {
	const targetSchema = prefix ? navigateToField(schema, prefix) : schema;
	if (!targetSchema) return [];
	const targetValues = prefix ? getNestedValue(values, prefix) : values;

	const paths = getRequiredForApprovalFieldPaths(targetSchema, targetValues);
	const labels: string[] = [];
	for (const path of paths) {
		if (!isEmpty(getNestedValue(targetValues, path))) continue;
		const field = navigateToField(targetSchema, path);
		if (!field) continue;
		const meta = field.meta() as { label?: string } | undefined;
		labels.push(meta?.label ?? path.split(".").pop() ?? path);
	}
	return [...new Set(labels)];
};
